import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchSLAPolicies,
  createSLAPolicy,
  updateSLAPolicy,
  fetchTicketTypes,
} from '../../../entities/helpdesk-settings/model';
import { SettingsToggle } from '@shared/ui/settings-toggle';
import { SettingsToggleWithInput } from '@shared/ui/settings-toggle';
import type { SLAPolicy, TicketType } from '../../../types/helpdeskSettingsInterfaces';

/* ── Хелперы для отображения ── */
const formatMinutes = (min: number): string => {
  if (min < 60) return `${min} мин`;
  if (min < 1440) {
    const h = Math.floor(min / 60);
    return `${h} час${h === 1 ? '' : h < 5 ? 'а' : 'ов'}`;
  }
  const d = Math.floor(min / 1440);
  return `${d} ${d === 1 ? 'день' : d < 5 ? 'дня' : 'дней'}`;
};

const conditionText = (
  conditions: Record<string, any>,
  ticketTypes: TicketType[] = [],
): string => {
  if (!conditions || Object.keys(conditions).length === 0) return 'все остальные заявки';
  const parts: string[] = [];
  if (conditions.priority) parts.push(`приоритет = ${conditions.priority}`);
  if (conditions.channel) parts.push(`канал = ${conditions.channel}`);
  if (conditions.company_id) parts.push(`компания #${conditions.company_id}`);
  if (conditions.ticket_type) {
    const t = ticketTypes.find((tt) => tt.id === conditions.ticket_type);
    parts.push(`тип = ${t ? t.name : `#${conditions.ticket_type}`}`);
  }
  return parts.length ? parts.join(', ') : 'все остальные заявки';
};

/* ── Время-опции для select ── */
const TIME_OPTIONS = [
  { value: 15, label: '15 мин' },
  { value: 30, label: '30 мин' },
  { value: 60, label: '1 час' },
  { value: 120, label: '2 часа' },
  { value: 240, label: '4 часа' },
  { value: 480, label: '8 часов' },
  { value: 1440, label: '1 день' },
  { value: 2880, label: '2 дня' },
  { value: 4320, label: '3 дня' },
  { value: 7200, label: '5 дней' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'critical', label: 'Критический' },
  { value: 'high', label: 'Высокий' },
  { value: 'medium', label: 'Средний' },
  { value: 'low', label: 'Низкий' },
];

/* ── Модалка создания/редактирования ── */
interface SLAModalProps {
  policy?: SLAPolicy | null;
  ticketTypes: TicketType[];
  onClose: () => void;
  onSave: (data: Partial<SLAPolicy>) => void;
}

const SLAModal: React.FC<SLAModalProps> = ({ policy, ticketTypes, onClose, onSave }) => {
  const [name, setName] = useState(policy?.name || '');
  const [priority, setPriority] = useState(policy?.conditions?.priority || '');
  const [ticketType, setTicketType] = useState<number | ''>(
    policy?.conditions?.ticket_type || '',
  );
  const [firstResponse, setFirstResponse] = useState(policy?.first_response_min || 30);
  const [resolution, setResolution] = useState(policy?.resolution_min || 240);
  const [businessHours, setBusinessHours] = useState(policy?.business_hours_only ?? true);
  const [isActive, setIsActive] = useState(policy?.is_active ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const conditions: Record<string, any> = {};
    if (priority) conditions.priority = priority;
    if (ticketType) conditions.ticket_type = ticketType;

    const typeName = ticketType
      ? ticketTypes.find((t) => t.id === ticketType)?.name
      : undefined;
    const autoName =
      typeName ||
      (priority
        ? `${PRIORITY_OPTIONS.find((o) => o.value === priority)?.label || priority} приоритет`
        : 'Стандартный');

    onSave({
      name: name || autoName,
      conditions,
      first_response_min: firstResponse,
      resolution_min: resolution,
      business_hours_only: businessHours,
      is_active: isActive,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {policy ? 'Редактировать политику' : 'Добавить политику'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Приоритет</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Тип заявки</label>
              <select
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="">Любой</option>
                {ticketTypes.filter((t) => t.is_active).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Условие</label>
            <input
              value={conditionText(
                {
                  ...(priority ? { priority } : {}),
                  ...(ticketType ? { ticket_type: ticketType } : {}),
                },
                ticketTypes,
              )}
              disabled
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Первый ответ*</label>
              <select
                value={firstResponse}
                onChange={(e) => setFirstResponse(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                {TIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Решение*</label>
              <select
                value={resolution}
                onChange={(e) => setResolution(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                {TIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Рабочее время*</label>
              <select
                value={businessHours ? 'yes' : 'no'}
                onChange={(e) => setBusinessHours(e.target.value === 'yes')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="yes">Да</option>
                <option value="no">Нет</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Статус*</label>
              <select
                value={isActive ? 'active' : 'inactive'}
                onChange={(e) => setIsActive(e.target.value === 'active')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="active">Активна</option>
                <option value="inactive">Неактивна</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Отмена
            </button>
            <button type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022]">
              {policy ? 'Сохранить' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const SLASettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings, status, slaPolicies, slaStatus, ticketTypes, ticketTypesStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<SLAPolicy | null>(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchHelpdeskSettings());
    if (slaStatus === 'idle') dispatch(fetchSLAPolicies());
    // Типы нужны для условия «Тип заявки» в модалке и для подписи условий.
    if (ticketTypesStatus === 'idle') dispatch(fetchTicketTypes());
  }, [dispatch, status, slaStatus, ticketTypesStatus]);

  const handleSettingsUpdate = useCallback(
    (field: string, value: boolean | number) => {
      dispatch(updateHelpdeskSettings({ [field]: value }));
    },
    [dispatch],
  );

  const handleSavePolicy = useCallback(
    async (data: Partial<SLAPolicy>) => {
      if (editingPolicy) {
        await dispatch(updateSLAPolicy({ id: editingPolicy.id, data }));
      } else {
        await dispatch(createSLAPolicy(data));
      }
      setModalOpen(false);
      setEditingPolicy(null);
    },
    [dispatch, editingPolicy],
  );

  const openEdit = (policy: SLAPolicy) => {
    setEditingPolicy(policy);
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingPolicy(null);
    setModalOpen(true);
  };

  if ((status === 'loading' && !settings) || (slaStatus === 'loading' && slaPolicies.length === 0)) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-32 bg-gray-100 rounded-lg"></div>
            <div className="h-32 bg-gray-100 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {/* Заголовок «SLA-политики» вынесен в шапку приложения. Подсказка
            про порядок применения остаётся — она важна функционально. */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            Применяются по первому совпадению условий
          </p>
          <button onClick={openCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors">
            + Добавить политику
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {slaPolicies.map((policy) => (
            <div key={policy.id} onClick={() => openEdit(policy)}
              className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#2d2c2c]">{policy.name}</h3>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  policy.is_active
                    ? 'bg-green-50 text-green-600 border border-green-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-200'
                }`}>
                  {policy.is_active ? 'Активна' : 'Неактивна'}
                </span>
              </div>
              <p className="text-xs text-[#e3282a] mb-3">
                Условие: {conditionText(policy.conditions, ticketTypes)}
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-gray-400">Первый ответ</p>
                  <p className="text-sm font-semibold text-[#2d2c2c]">{formatMinutes(policy.first_response_min)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Решение</p>
                  <p className="text-sm font-semibold text-[#2d2c2c]">{formatMinutes(policy.resolution_min)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Рабочее время</p>
                  <p className="text-sm font-semibold text-[#2d2c2c]">{policy.business_hours_only ? 'Да' : 'Нет'}</p>
                </div>
              </div>
            </div>
          ))}

          <div onClick={openCreate}
            className="border border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all min-h-[120px]">
            <span className="text-sm text-gray-400 font-medium">+ Добавить политику</span>
          </div>
        </div>
      </div>

      {settings && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-[#2d2c2c] mb-4">Уведомления о нарушении SLA</h2>
          <div>
            <SettingsToggleWithInput
              label="Предупреждение до истечения дедлайна"
              description="Уведомить назначенного оператора и ведущего оператора"
              checked={settings.sla_warn_enabled}
              onToggle={(v) => handleSettingsUpdate('sla_warn_enabled', v)}
              value={settings.sla_warn_before_minutes}
              onValueChange={(v) => handleSettingsUpdate('sla_warn_before_minutes', v)}
              prefix="За"
              unit="мин"
              max={480}
            />
            <SettingsToggle
              label="Уведомления при нарушении SLA"
              description="Уведомить оператора, ведущего оператора и администратора"
              checked={settings.sla_breach_notify_enabled}
              onChange={(v) => handleSettingsUpdate('sla_breach_notify_enabled', v)}
            />
          </div>
        </div>
      )}

      {modalOpen && (
        <SLAModal policy={editingPolicy}
          ticketTypes={ticketTypes}
          onClose={() => { setModalOpen(false); setEditingPolicy(null); }}
          onSave={handleSavePolicy} />
      )}
    </div>
  );
};

export default SLASettings;
